import { ListRenderItem } from '@shopify/flash-list';
import { FileAddButtons } from 'components/Field/FileAddButtons';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import {
  NAVIGATION_HEADER_HEIGHT,
  NavigationHeader,
} from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import { Txt } from 'components/Txt';
import { format } from 'date-fns';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import * as React from 'react';
import { LayoutChangeEvent, Platform, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { FileType, FileTypeGroupsEnum } from 'service/file/types';
import {
  doFileDownload,
  doFilePreview,
  doFilesDeleteFile,
} from 'store/actions';
import {
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
  eventTypeFilesSelected,
} from 'store/app/actions';
import {
  selectFileImageGridFit,
  selectFilesContext,
  selectFilesFiles,
  selectFilesImageGridSize,
  selectFilesImageRemoteUrl,
  selectFilesShowImageFilename,
  selectFilesViewType,
} from 'store/files/selectors';

import { theme } from 'lib/theme';
import { SortMenu } from 'components/SortMenu';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useScreenSize } from 'hooks/useScreenSize';
import { ScreenSize } from 'lib/constants';
import {
  FilesContextEnum,
  GENERIC_IMAGE_GRID_FIT_FILE_MANAGER,
  GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER,
  ImageGridFitEnums,
  ImageGridSizes,
} from 'types/types';
import { Menu, MenuProps } from 'components/Menu';
import { canPreviewFileGroup, filterArrayEmpty } from 'lib/riskbase';
import { TextField } from 'components/Field/TextField';
import { smartText } from 'lib/str';
import { FileManagerFilesListItem } from './FileManagerFilesListItem';
import {
  FileManagerShowImageFilenames,
  FileManagerViewType,
  GenericKeyFileManagerShowImageFilenames,
  GenericKeyFileManagerViewType,
} from './types';

enum FileSortTypes {
  NAMEASC = 'name:asc',
  NAMEDESC = 'name:desc',
  DATEDESC = 'date:desc',
  DATEASC = 'date:asc',
}

type ListItemFile = {
  id: string;
  assetId: string;
  name: string;
  timestamp: number;
  editable: boolean;
  deletable: boolean;
  type: FileType;
  selectable: boolean;
  selected: boolean;
};

export const FileManagerFilesScreen: React.FC<
  ModalScreenProps<ModalScreens.FILEMANAGER>
> = ({ navigation, route }) => {
  const {
    eventKey,
    multiSelect,
    promptMessage,
    limitSelectionToFileGroups = Object.values(FileTypeGroupsEnum),
  } = route.params;

  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const [listWidth, setListWidth] = React.useState(0);
  const screenSize = useScreenSize();
  const context = useSelector(selectFilesContext);
  const assetId = context?.assetId || '-1';
  const imageRemoteUrl = useSelector((state) =>
    selectFilesImageRemoteUrl(state, 's'),
  );
  const imageGridSize = useSelector(selectFilesImageGridSize);
  const imageGridFit = useSelector(selectFileImageGridFit);
  const showImageFilenames = useSelector(selectFilesShowImageFilename);
  const viewType = useSelector(selectFilesViewType);
  const files = useSelector((state) => selectFilesFiles(state));

  const [filesAreLoaded, setFilesAreLoaded] = React.useState(false);
  React.useEffect(() => {
    if (!filesAreLoaded) {
      setTimeout(() => setFilesAreLoaded(true), 250);
    }
  }, [dispatch, filesAreLoaded]);

  React.useEffect(() => {
    return () => {
      // dispatch files closed
    };
  }, [dispatch]);

  const [sortFile, setSortFile] = React.useState<FileSortTypes>(
    FileSortTypes.DATEDESC,
  );
  const [selectedFiles, setSelectedFiles] = React.useState<Set<string>>(
    new Set(),
  );
  const [searchValue, setSearchValue] = React.useState('');

  const selectedFileIds = Array.from(selectedFiles);

  const hasSelectedFiles = !!selectedFiles.size;

  const itemGap = theme.paddingHorizontalHalf / 2;
  const calculatedColumns = Math.max(
    Math.round(listWidth / imageGridSize.idealWidth),
    1,
  );
  const tileWidth = Math.round(listWidth / calculatedColumns) - itemGap * 2;

  const handleChooseSelected = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      eventTypeFilesSelected({
        eventKey,
        fileIds: Array.from(selectedFiles),
      }),
    );
  }, [dispatch, eventKey, selectedFiles]);

  const handleClose = React.useCallback(() => {
    if (hasSelectedFiles) {
      confirmUnsaved({
        text: 'Your selection will be discarded',
        onDiscardText: 'Discard Selection',
        onSaveText: 'Use Selection',
        onSave: handleChooseSelected,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleChooseSelected, hasSelectedFiles]);

  const handlePressListItem = React.useCallback(
    (item: ListItemFile) => () =>
      setSelectedFiles((prevSelectedFiles) => {
        if (prevSelectedFiles.has(item.id)) {
          prevSelectedFiles.delete(item.id);
        } else {
          if (!multiSelect) {
            prevSelectedFiles.clear();
          }
          prevSelectedFiles.add(item.id);
        }
        return new Set(prevSelectedFiles);
      }),
    [multiSelect],
  );

  const handlePreviewFile = React.useCallback(
    (item: ListItemFile) => () => {
      if (!context) {
        return;
      }
      dispatch(
        doFilePreview({
          fileId: item.id,
          typeId: item.type.extension,
          context,
        }),
      );
    },
    [context, dispatch],
  );

  const handleDownloadFile = React.useCallback(
    (item: ListItemFile) => () => {
      if (!context) {
        return;
      }
      dispatch(
        doFileDownload({
          fileId: item.id,
          typeId: item.type.extension,
          context,
        }),
      );
    },
    [context, dispatch],
  );

  const handleDeleteListItem = React.useCallback(
    (item: ListItemFile) => () =>
      assetId
        ? confirm({
            confirmText: 'Delete',
            destructive: true,
            onConfirm: () =>
              dispatch(
                doFilesDeleteFile({
                  fileId: item.id,
                }),
              ),
            text: 'Are you sure you want to delete this file?',
            title: 'Delete File',
          })
        : null,
    [assetId, confirm, dispatch],
  );

  // Create raw data array
  const dataRaw = React.useMemo(
    (): ListItemFile[] =>
      files.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type,
        timestamp: file.timestamp,
        deletable: file.deletable,
        editable: file.editable,
        selectable: limitSelectionToFileGroups.includes(file.type.group),
        assetId: assetId || '',
        selected: false,
      })),
    [files, assetId, limitSelectionToFileGroups],
  );

  // Sort data
  const dataSorted = React.useMemo((): ListItemFile[] => {
    const dSorted = [...dataRaw];
    switch (sortFile) {
      case FileSortTypes.NAMEASC:
        return naturalOrderBy(dSorted, (v) => v.name);
      case FileSortTypes.NAMEDESC:
        return naturalOrderBy(dSorted, (v) => v.name, 'desc');
      case FileSortTypes.DATEDESC:
        return dSorted.sort((a, b) => b.timestamp - a.timestamp);
      case FileSortTypes.DATEASC:
      default:
        return dSorted.sort((a, b) => a.timestamp - b.timestamp);
    }
  }, [dataRaw, sortFile]);

  // Add selected state to data
  const filteredData = React.useMemo((): ListItemFile[] => {
    let withSelected = !selectedFileIds.length
      ? dataSorted
      : dataSorted.map<ListItemFile>((file) => ({
          id: file.id,
          assetId: file.assetId,
          name: file.name,
          selectable: file.selectable,
          type: file.type,
          timestamp: file.timestamp,
          deletable: file.deletable,
          editable: file.editable,
          selected: selectedFileIds.includes(file.id),
        }));
    const searchTerms = searchValue
      .split(' ')
      .map((a) => a.trim().toLowerCase())
      .filter(filterArrayEmpty);
    if (searchTerms.length) {
      withSelected = withSelected.filter((file) => {
        const finalSearchString = [
          file.name,
          file.type.extension,
          file.type.name,
        ]
          .join(' ')
          .toLowerCase();
        return searchTerms.every((term) => finalSearchString.includes(term));
      });
    }
    return withSelected;
  }, [dataSorted, searchValue, selectedFileIds]);

  const renderItem: ListRenderItem<ListItemFile> = React.useCallback(
    ({ item, extraData }) => {
      const options: MenuProps['options'] = [];
      if (canPreviewFileGroup(item.type.group)) {
        options.push({
          text: 'Preview',
          onPress: handlePreviewFile(item),
          icon: ICONS.EyeLight,
        });
      }
      options.push({
        text: Platform.OS === 'web' ? 'Download' : 'Share',
        icon: ICONS.ShareOS,
        onPress: handleDownloadFile(item),
      });
      if (
        context?.context === FilesContextEnum.localAsset ||
        context?.context === FilesContextEnum.localAssessment
      ) {
        if (options.length) {
          options.push('-');
        }
        options.push({
          text: 'Delete',
          icon: ICONS.TrashCanLight,
          onPress: item.deletable ? handleDeleteListItem(item) : undefined,
          destructive: true,
        });
      }
      options.push('-');
      options.push(`${item.name.trim()}`);
      options.push(
        `${item.type.extension.toUpperCase()} • ${format(
          new Date(item.timestamp * 1000),
          'd MMMM yyyy',
        )}`,
      );
      return (
        <FileManagerFilesListItem
          onPress={handlePressListItem(item)}
          selectable={item.selectable}
          key={item.id}
          fileType={item.type}
          title={item.name.trim()}
          menuOptions={options}
          thumbnail={
            imageRemoteUrl
              ? {
                  imageUrl: imageRemoteUrl(item.id)!,
                }
              : {
                  thumbnailId: item.id,
                }
          }
          chosen={item.selected}
          viewType={extraData.viewType}
          fileTimestamp={item.timestamp}
          imageWidth={extraData.imageWidth}
          itemGap={extraData.itemGap}
        />
      );
    },
    [
      context?.context,
      handleDeleteListItem,
      handleDownloadFile,
      handlePressListItem,
      handlePreviewFile,
      imageRemoteUrl,
    ],
  );

  const handleSetView = (newValue: FileManagerViewType) => {
    dispatch(
      SET_GENERIC({
        key: GenericKeyFileManagerViewType,
        value: newValue,
      }),
    );
  };

  const handleSetShowImageFilenames = (newValue: 'show' | 'hide') => {
    dispatch(
      SET_GENERIC({
        key: GenericKeyFileManagerShowImageFilenames,
        value: newValue,
      }),
    );
  };

  const handleSearchChange = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
  };

  const hasSelection = !!selectedFileIds.length;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasSelection });
  }, [navigation, hasSelection]);
  useEsc(handleClose);

  const filteredDataCount = filteredData.length;
  const SearchField = React.useMemo<React.ReactElement>(
    () => (
      <View
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: NAVIGATION_HEADER_HEIGHT,
          },
          screenSize === ScreenSize.small
            ? { paddingHorizontal: theme.paddingHorizontal }
            : { flex: 1 },
        ]}
      >
        <TextField
          placeholder="Search Files"
          value=""
          iconLeft={ICONS.MagnifyingGlassLight}
          onChange={handleSearchChange}
          onBlur={() => {}}
          clearable
          onChangeDebounceMs={300}
          containerStyle={{
            backgroundColor: '#ffffff',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
            flex: 1,
            overflow: 'hidden',
            height: NAVIGATION_HEADER_HEIGHT - theme.grid,
          }}
          inputStyle={{ minHeight: null }}
          disabled={!!selectedFiles.size}
          autoCorrect={false}
          autoComplete="off"
          autoCapitalize="none"
          onEsc={handleClose}
        />
        {!!filteredDataCount && (
          <View
            style={{
              paddingRight: theme.paddingHorizontal,
              paddingLeft: theme.paddingHorizontal,
              marginLeft: theme.paddingHorizontalHalf / 2,
              borderLeftColor: theme.colors.background,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              backgroundColor: '#fafafa',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 45,
              zIndex: 1,
              height: NAVIGATION_HEADER_HEIGHT - theme.grid,
            }}
          >
            <Txt
              size="xsmall"
              weight="bold"
              style={{ color: theme.colors.fieldValueText, marginTop: 1 }}
            >
              {pluralise(filteredDataCount, '{x}', '{x}')}
            </Txt>
          </View>
        )}
      </View>
    ),
    [filteredDataCount, handleClose, screenSize, selectedFiles.size],
  );

  const handleListLayout = React.useCallback((event: LayoutChangeEvent) => {
    setListWidth(event.nativeEvent.layout.width);
  }, []);

  if (!context) {
    return null;
  }

  return (
    <PageFlatList
      contentPaddingTop={theme.paddingHorizontal - itemGap}
      contentPaddingBottom={theme.paddingHorizontal - itemGap}
      contentPaddingLeft={theme.paddingHorizontal - itemGap}
      contentPaddingRight={theme.paddingHorizontal - itemGap}
      header={
        <ViewWithSeparators>
          <NavigationHeader
            heading="Asset Files"
            subheading={
              selectedFiles.size
                ? pluralise(selectedFiles.size, '1 File', '{x} Files')
                : promptMessage
            }
            onPressSaveButton={selectedFiles.size ? handleChooseSelected : null}
            saveButtonText="Done"
            onPressCancelButton={handleClose}
          />
          {context.context === FilesContextEnum.remoteAssessment && (
            <View style={{ padding: theme.paddingHorizontal }}>
              <Txt
                size="xsmall"
                style={{
                  color: theme.colors.textBlack64,
                }}
              >
                New files cannot be added in QC yet
              </Txt>
            </View>
          )}
          <ViewWithSeparators
            horizontal
            separatorMargin={theme.paddingHorizontalHalf}
            separatorColor="transparent"
            style={{
              alignItems: 'center',
              paddingVertical: theme.paddingHorizontalHalf,
              paddingHorizontal: theme.paddingHorizontal,
            }}
          >
            {screenSize === ScreenSize.large && (
              <View style={{ flex: 1 }}>{SearchField}</View>
            )}
            <FileAddButtons
              context={context}
              enableFileManager={false}
              multiSelect
              maximized
              disabled={!!selectedFiles.size}
            />
            {screenSize === ScreenSize.small && <View style={{ flex: 1 }} />}
            <SortMenu
              value={sortFile}
              options={[
                { id: FileSortTypes.NAMEASC, name: 'Name A-Z' },
                { id: FileSortTypes.NAMEDESC, name: 'Name Z-A' },
                { id: FileSortTypes.DATEDESC, name: 'New First' },
                { id: FileSortTypes.DATEASC, name: 'Old First' },
              ]}
              onChange={setSortFile}
              disabled={!!selectedFiles.size}
            />
            <Menu
              iconButtonConfig={{
                icon: ICONS.SlidersUpLight,
                disabled: !!selectedFiles.size,
              }}
              options={[
                {
                  key: 'viewAsList',
                  text: 'List',
                  onPress: () => handleSetView(FileManagerViewType.oneColumn),
                  leftIcon:
                    viewType === FileManagerViewType.oneColumn
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                  icon: ICONS.LineColumnOneLight,
                },
                {
                  key: 'viewAsListColumn',
                  text: 'Two Column List',
                  onPress:
                    screenSize === ScreenSize.small
                      ? undefined
                      : () => handleSetView(FileManagerViewType.twoColumn),
                  leftIcon:
                    viewType === FileManagerViewType.twoColumn
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                  icon: ICONS.LineColumnTwoLight,
                },
                {
                  key: 'viewAsGrid',
                  text: 'Grid',
                  onPress: () => handleSetView(FileManagerViewType.grid),
                  leftIcon:
                    viewType === FileManagerViewType.grid
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                  icon: ICONS.GridSolid,
                },
                'Thumbnail Size',
                ...Object.values(ImageGridSizes).map((size) => ({
                  key: size.name,
                  text: `${size.name}`,
                  leftIcon:
                    size.key === imageGridSize.key
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                  onPress: () =>
                    dispatch(
                      SET_GENERIC({
                        key: GENERIC_IMAGE_GRID_SIZE_FILE_MANAGER,
                        value: size.key,
                      }),
                    ),
                })),
                'Thumbnail Cropping',
                {
                  text: 'Square Photos',
                  onPress: () =>
                    dispatch(
                      SET_GENERIC({
                        key: GENERIC_IMAGE_GRID_FIT_FILE_MANAGER,
                        value: ImageGridFitEnums.square,
                      }),
                    ),
                  icon: ICONS.ImageFitSquareLight,
                  leftIcon:
                    ImageGridFitEnums.aspect !== imageGridFit
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                },
                {
                  text: 'Aspect Photos',
                  onPress: () =>
                    dispatch(
                      SET_GENERIC({
                        key: GENERIC_IMAGE_GRID_FIT_FILE_MANAGER,
                        value: ImageGridFitEnums.aspect,
                      }),
                    ),
                  icon: ICONS.ImageFitAspectLight,
                  leftIcon:
                    ImageGridFitEnums.aspect === imageGridFit
                      ? ICONS.CircleDotSolid
                      : ICONS.CircleLight,
                },
                'Image Filenames',
                {
                  text: 'Show Image Filenames',
                  subText: smartText('When using "Grid" view'),
                  onPress: () =>
                    handleSetShowImageFilenames(
                      showImageFilenames === FileManagerShowImageFilenames.show
                        ? FileManagerShowImageFilenames.hide
                        : FileManagerShowImageFilenames.show,
                    ),
                  leftIcon:
                    showImageFilenames === FileManagerShowImageFilenames.show
                      ? ICONS.SquareCheckSolid
                      : ICONS.SquareLight,
                },
              ]}
            />
          </ViewWithSeparators>
          {screenSize === ScreenSize.small && SearchField}
        </ViewWithSeparators>
      }
      emptyConfig={{
        icon: filesAreLoaded ? ICONS.FolderOpenLight : undefined,
      }}
      isLoading={!filesAreLoaded}
      listConfig={{
        onLayout: handleListLayout,
        data: filesAreLoaded ? filteredData : [],
        renderItem,
        keyExtractor: (item) => item.id,
        extraData: {
          imageWidth:
            viewType === FileManagerViewType.grid
              ? tileWidth
              : imageGridSize.idealWidth,
          itemGap,
          viewType,
        },
        numberOfColumns:
          viewType === FileManagerViewType.grid
            ? calculatedColumns
            : viewType === FileManagerViewType.oneColumn
              ? 1
              : 2,
      }}
    />
  );
};
